import { useEffect, useState } from "react";
import type { TaskFilter as TaskFilterType, TaskPriority, TaskStatus } from "../types";

interface TaskFilterProps {
	filter: TaskFilterType;
	onFilterChange: (filter: TaskFilterType) => void;
}

export function TaskFilter({ filter, onFilterChange }: TaskFilterProps) {
	const [searchInput, setSearchInput] = useState(filter.search || "");

	useEffect(() => {
		const timer = setTimeout(() => {
			if (searchInput !== filter.search) {
				onFilterChange({ ...filter, search: searchInput || undefined });
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [searchInput, filter, onFilterChange]);

	const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as TaskStatus | "";
		onFilterChange({
			...filter,
			status: value || undefined,
		});
	};

	const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value as TaskPriority | "";
		onFilterChange({
			...filter,
			priority: value || undefined,
		});
	};

	const handleClearFilters = () => {
		setSearchInput("");
		onFilterChange({});
	};

	const hasActiveFilters = filter.status || filter.priority || filter.search;

	return (
		<div className="card p-4">
			<div className="flex flex-col gap-4">
				<div className="flex flex-col sm:flex-row gap-4">
					<div className="flex-1">
						<label htmlFor="search" className="label">
							検索
						</label>
						<div className="relative">
							<input
								type="text"
								id="search"
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
								placeholder="タスク名で検索..."
								className="input pl-10"
							/>
							<svg
								className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
								/>
							</svg>
						</div>
					</div>

					<div className="w-full sm:w-40">
						<label htmlFor="status" className="label">
							ステータス
						</label>
						<select
							id="status"
							value={filter.status || ""}
							onChange={handleStatusChange}
							className="input"
						>
							<option value="">すべて</option>
							<option value="todo">未着手</option>
							<option value="in_progress">進行中</option>
							<option value="done">完了</option>
						</select>
					</div>

					<div className="w-full sm:w-40">
						<label htmlFor="priority" className="label">
							優先度
						</label>
						<select
							id="priority"
							value={filter.priority || ""}
							onChange={handlePriorityChange}
							className="input"
						>
							<option value="">すべて</option>
							<option value="high">高</option>
							<option value="medium">中</option>
							<option value="low">低</option>
						</select>
					</div>
				</div>

				{hasActiveFilters && (
					<div className="flex justify-end">
						<button
							type="button"
							onClick={handleClearFilters}
							className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
						>
							フィルターをクリア
						</button>
					</div>
				)}
			</div>
		</div>
	);
}
